import { NavigateOptions, To, useNavigate } from 'react-router-dom';
import { mergeSearchParams } from '../utils/routes';

/**
 * Hook that extends useNavigate to carry the current query parameters
 * forward by default. Both string and object targets route through
 * `mergeSearchParams`, which also strips one-shot params (e.g. `focus`)
 * from the current URL so a single deep-link landing doesn't leak its
 * hints into every subsequent click.
 *
 * @returns `{ navigateWithQueryParams }` — same `(to, options)` signature as
 *   `useNavigate`. A query embedded in a string target (e.g.
 *   `'/findings?filter=open'`) is honored: its params overlay the cleaned
 *   current ones, not the other way around.
 */
export const useNavigateWithQueryParams = () => {
  const navigate = useNavigate();

  const navigateWithQueryParams = (to: To, options?: NavigateOptions) => {
    if (typeof to === 'string') {
      // Split the target's own query out of the path so `pathname` stays
      // path-only — `mergeSearchParams` reads `to`'s embedded query directly
      // from the string and overlays it on the cleaned current params.
      const qIdx = to.indexOf('?');
      const pathname = qIdx >= 0 ? to.slice(0, qIdx) : to;

      return navigate({ pathname, search: mergeSearchParams(to) }, options);
    }

    return navigate({ ...to, search: mergeSearchParams(to) }, options);
  };

  return { navigateWithQueryParams };
};
